import { useEffect, useState } from "react";
import { Hash, LogOut, MessageCircle, QrCode, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useWhatsappSession } from "@/lib/hooks/useWhatsapp";
import { useConnectionActions, useConnectionSession, ConnectionRpcError } from "@/lib/hooks/useConnections";
import {
  buildE164,
  connectionErrorMessage,
  deriveChannelState,
  formatPairingCode,
  isDroppedSession,
  isProcessing,
  secondsUntil,
  type ConnectionMethod,
  type ConnectionSession,
} from "@/lib/connections";
import { CardShell, ChannelStatusBadge, LoadError, Notice, PhoneField, Processing, StepList } from "./shared";
import { QrCodeSvg } from "./QrCodeSvg";

type Actions = ReturnType<typeof useConnectionActions>;
type SetupStep = "intro" | "risk" | "method";

export function WhatsappConnectionCard(): JSX.Element {
  const rowQuery = useWhatsappSession();
  const sessionQuery = useConnectionSession("whatsapp");
  const row = rowQuery.data;
  const session = sessionQuery.data;
  const loadFailed = rowQuery.isError || sessionQuery.isError;
  const retry = (): void => {
    void rowQuery.refetch();
    void sessionQuery.refetch();
  };
  const actions = useConnectionActions("whatsapp");
  const state = loadFailed ? "error" : deriveChannelState(row, session);
  const sessionError = session?.status === "failed" ? connectionErrorMessage(session.error) : null;
  const dropped = isDroppedSession(row) ? "A sessão do WhatsApp caiu. Gere um novo código para reconectar." : null;

  return (
    <CardShell id="card-whatsapp" icon={MessageCircle} title="WhatsApp" badge={<ChannelStatusBadge state={state} />}>
      {loadFailed ? (
        <LoadError onRetry={retry} retrying={rowQuery.isFetching || sessionQuery.isFetching} />
      ) : (
        <>
          {state === "connecting" && session && <WhatsappPairing session={session} actions={actions} />}
          {state === "connected" && row && (
            <ConnectedView phone={row.phone} since={row.last_connected_at} actions={actions} />
          )}
          {(state === "disconnected" || state === "error") && (
            <WhatsappSetup actions={actions} notice={sessionError ?? dropped} />
          )}
        </>
      )}
    </CardShell>
  );
}

function WhatsappSetup({ actions, notice }: { actions: Actions; notice: string | null }): JSX.Element {
  const [step, setStep] = useState<SetupStep>("intro");
  const [accepted, setAccepted] = useState(false);

  if (step === "intro") {
    return (
      <>
        <p className="text-sm text-muted-foreground">
          Conecte um número de WhatsApp para monitorar grupos e repostar ofertas com o seu link de afiliado.
        </p>
        {notice && <Notice tone="danger">{notice}</Notice>}
        <div className="flex justify-end">
          <Button type="button" variant="gold" onClick={() => setStep("risk")}>
            <MessageCircle size={15} />
            Conectar WhatsApp
          </Button>
        </div>
      </>
    );
  }
  if (step === "risk") {
    return (
      <RiskStep accepted={accepted} onAcceptedChange={setAccepted} onBack={() => setStep("intro")} onNext={() => setStep("method")} />
    );
  }
  return <MethodStep actions={actions} onBack={() => setStep("risk")} />;
}

function StepHeader({ step, title }: { step: number; title: string }): JSX.Element {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--gold))]">Passo {step} de 3</p>
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
    </div>
  );
}

interface RiskStepProps {
  accepted: boolean;
  onAcceptedChange: (value: boolean) => void;
  onBack: () => void;
  onNext: () => void;
}

function RiskStep({ accepted, onAcceptedChange, onBack, onNext }: RiskStepProps): JSX.Element {
  return (
    <div className="space-y-4">
      <StepHeader step={1} title="Antes de conectar" />
      <ul className="space-y-2.5 text-sm text-muted-foreground">
        <li className="flex gap-2.5">
          <Smartphone size={16} className="mt-0.5 shrink-0 text-[hsl(var(--gold))]" aria-hidden="true" />
          <span>
            <strong className="text-foreground">Use um número secundário</strong>, separado do seu WhatsApp pessoal ou de atendimento.
          </span>
        </li>
        <li className="flex gap-2.5">
          <Hash size={16} className="mt-0.5 shrink-0 text-[hsl(var(--gold))]" aria-hidden="true" />
          <span>
            A conexão usa uma <strong className="text-foreground">API não oficial</strong> do WhatsApp. O WhatsApp pode
            restringir ou banir o número, principalmente com envios em massa.
          </span>
        </li>
        <li className="flex gap-2.5">
          <X size={16} className="mt-0.5 shrink-0 text-[hsl(var(--gold))]" aria-hidden="true" />
          <span>
            Não use o mesmo número em outras ferramentas de automação (Z-API, Evolution, WPPConnect e similares). Duas
            conexões no mesmo número derrubam uma à outra.
          </span>
        </li>
      </ul>
      <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-[hsl(var(--gold)/0.3)] bg-[hsl(var(--gold)/0.06)] px-3 py-3 text-sm text-foreground">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-[hsl(var(--gold))]"
          checked={accepted}
          onChange={(e) => onAcceptedChange(e.target.checked)}
        />
        <span>Li e entendi os riscos de banimento e quero conectar mesmo assim.</span>
      </label>
      <div className="flex justify-between gap-2">
        <Button type="button" variant="ghost" onClick={onBack}>
          Voltar
        </Button>
        <Button type="button" variant="gold" onClick={onNext} disabled={!accepted}>
          Continuar
        </Button>
      </div>
    </div>
  );
}

const METHODS: ReadonlyArray<{ id: ConnectionMethod; title: string; hint: string; icon: typeof Hash }> = [
  { id: "pairing_code", title: "Código de pareamento", hint: "Digite um código de 8 caracteres no celular.", icon: Hash },
  { id: "qr", title: "QR Code", hint: "Escaneie o QR com a câmera do WhatsApp.", icon: QrCode },
];

function MethodStep({ actions, onBack }: { actions: Actions; onBack: () => void }): JSX.Element {
  const [method, setMethod] = useState<ConnectionMethod>("pairing_code");
  const [countryCode, setCountryCode] = useState("+55");
  const [phone, setPhone] = useState("");
  const e164 = buildE164(countryCode, phone);
  const rpcError = actions.start.error instanceof ConnectionRpcError ? actions.start.error.code : actions.start.error ? "unknown" : null;

  const submit = (): void => {
    if (!e164) return;
    actions.start.mutate({ phone: e164, method, riskAccepted: true });
  };

  return (
    <div className="space-y-4">
      <StepHeader step={2} title="Como você quer conectar?" />
      <div role="radiogroup" aria-label="Método de conexão" className="grid gap-2 sm:grid-cols-2">
        {METHODS.map((m) => (
          <MethodOption key={m.id} option={m} selected={method === m.id} onSelect={() => setMethod(m.id)} />
        ))}
      </div>
      <PhoneField
        idPrefix="wa"
        countryCode={countryCode}
        phone={phone}
        onCountryChange={setCountryCode}
        onPhoneChange={setPhone}
        disabled={actions.start.isPending}
      />
      {phone.trim() && !e164 && <p className="text-xs text-destructive">Número incompleto. Confira o DDD e o número.</p>}
      {rpcError && <Notice tone="danger">{connectionErrorMessage(rpcError)}</Notice>}
      <div className="flex justify-between gap-2">
        <Button type="button" variant="ghost" onClick={onBack}>
          Voltar
        </Button>
        <Button type="button" variant="gold" onClick={submit} disabled={!e164 || actions.start.isPending}>
          {method === "qr" ? "Gerar QR Code" : "Gerar código"}
        </Button>
      </div>
    </div>
  );
}

interface MethodOptionProps {
  option: (typeof METHODS)[number];
  selected: boolean;
  onSelect: () => void;
}

function MethodOption({ option, selected, onSelect }: MethodOptionProps): JSX.Element {
  const Icon = option.icon;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        "flex items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected
          ? "border-[hsl(var(--gold)/0.6)] bg-[hsl(var(--gold)/0.08)]"
          : "border-border hover:border-[hsl(var(--border-strong))]",
      )}
    >
      <Icon size={18} className={cn("mt-0.5 shrink-0", selected ? "text-[hsl(var(--gold))]" : "text-muted-foreground")} />
      <span className="space-y-0.5">
        <span className="block text-sm font-semibold text-foreground">{option.title}</span>
        <span className="block text-xs text-muted-foreground">{option.hint}</span>
      </span>
    </button>
  );
}

function useCountdown(expiresAt: string): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return secondsUntil(expiresAt, now);
}

function WhatsappPairing({ session, actions }: { session: ConnectionSession; actions: Actions }): JSX.Element {
  const left = useCountdown(session.expires_at);
  const isQr = session.method === "qr";
  const ready = isQr ? !!session.payload?.qr_data : !!session.payload?.pairing_code;

  return (
    <div className="space-y-4">
      <StepHeader step={3} title={isQr ? "Escaneie o QR Code" : "Digite o código no celular"} />
      {!ready || isProcessing(session) ? (
        <Processing>{isQr ? "Gerando o QR Code..." : "Gerando o código de pareamento..."}</Processing>
      ) : (
        <PairingChallenge session={session} isQr={isQr} />
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span aria-live="polite">
          {session.phone_masked ? `Número ${session.phone_masked}. ` : ""}Tempo restante: {Math.floor(left / 60)}:
          {String(left % 60).padStart(2, "0")}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => actions.cancel.mutate({ sessionId: session.id })}
          disabled={actions.cancel.isPending}
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function PairingChallenge({ session, isQr }: { session: ConnectionSession; isQr: boolean }): JSX.Element {
  if (isQr) {
    return (
      <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
        <div className="mx-auto">
          <QrCodeSvg value={session.payload?.qr_data ?? ""} label="QR Code para conectar o WhatsApp" />
        </div>
        <StepList
          steps={[
            "Abra o WhatsApp no celular do número secundário.",
            "Toque em Mais opções ou Configurações e depois em Aparelhos conectados.",
            "Toque em Conectar aparelho e aponte a câmera para o QR Code.",
            "O QR é renovado sozinho a cada 20 segundos.",
          ]}
        />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <p
        className="rounded-lg border border-[hsl(var(--gold)/0.35)] bg-[hsl(var(--gold)/0.07)] py-4 text-center font-mono text-3xl font-semibold tracking-[0.25em] text-[hsl(var(--gold))]"
        aria-label="Código de pareamento"
      >
        {formatPairingCode(session.payload?.pairing_code)}
      </p>
      <StepList
        steps={[
          "Abra o WhatsApp no celular do número secundário.",
          "Vá em Aparelhos conectados e toque em Conectar aparelho.",
          "Toque em Conectar com número de telefone e digite o código acima.",
          "O código vale por cerca de 1 minuto. Se expirar, cancele e gere outro.",
        ]}
      />
    </div>
  );
}

interface ConnectedViewProps {
  phone: string;
  since: string | null;
  actions: Actions;
}

function ConnectedView({ phone, since, actions }: ConnectedViewProps): JSX.Element {
  const confirmAndDisconnect = (): void => {
    if (window.confirm("Desconectar o WhatsApp? O monitoramento dos grupos para até você conectar de novo.")) {
      actions.disconnect.mutate();
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1 space-y-0.5 text-sm">
        <p className="font-mono text-foreground">{phone}</p>
        {since && (
          <p className="text-xs text-muted-foreground">Conectado desde {new Date(since).toLocaleDateString("pt-BR")}</p>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={confirmAndDisconnect}
        disabled={actions.disconnect.isPending}
        className="gap-1.5 text-destructive hover:text-destructive"
      >
        <LogOut size={14} />
        {actions.disconnect.isPending ? "Desconectando..." : "Desconectar"}
      </Button>
      {actions.disconnect.error && <Notice tone="danger">Não foi possível desconectar agora. Tente de novo.</Notice>}
    </div>
  );
}
